import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { subscriptionsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const Subscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const { isAdmin } = useAuth();

  const statusFilter = searchParams.get('status') || '';

  useEffect(() => {
    fetchSubscriptions();
  }, [statusFilter]);

  const fetchSubscriptions = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const res = await subscriptionsAPI.getAll(params);
      setSubscriptions(res.data);
    } catch (error) {
      toast.error('Gagal memuat data langganan');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSubscriptions();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus langganan ini?')) {
      try {
        await subscriptionsAPI.delete(id);
        toast.success('Langganan berhasil dihapus');
        fetchSubscriptions();
      } catch (error) {
        toast.error('Gagal menghapus langganan');
      }
    }
  };

  const handleStatusFilter = (status) => {
    if (status) {
      setSearchParams({ status });
    } else {
      setSearchParams({});
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Langganan Zoom</h1>
        {isAdmin && (
          <Link to="/subscriptions/new" className="btn-primary text-sm sm:text-base">
            + Tambah
          </Link>
        )}
      </div>

      <div className="card">
        <form onSubmit={handleSearch} className="flex gap-2 mb-4">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari email atau nama..."
            className="input-field flex-1 text-sm"
          />
          <button type="submit" className="btn-primary text-sm px-3">Cari</button>
        </form>

        <div className="flex gap-1 sm:gap-2 mb-4 overflow-x-auto pb-2">
          {[
            { key: '', label: 'Semua', color: 'primary' },
            { key: 'active', label: 'Aktif', color: 'green' },
            { key: 'expiring_soon', label: 'Habis', color: 'yellow' },
            { key: 'expired', label: 'Berakhir', color: 'red' }
          ].map(f => (
            <button
              key={f.key}
              onClick={() => handleStatusFilter(f.key)}
              className={`px-3 py-1.5 text-xs sm:text-sm rounded-lg whitespace-nowrap transition-colors ${
                statusFilter === f.key
                  ? `bg-${f.color}-600 text-white`
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {subscriptions.length === 0 ? (
          <p className="text-gray-500 text-center py-8 text-sm">Tidak ada data ditemukan</p>
        ) : (
          <>
            <div className="space-y-3 sm:hidden">
              {subscriptions.map((sub) => (
                <div key={sub.id} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-sm">{sub.user?.name}</p>
                      <p className="text-xs text-gray-500">{sub.zoomAccountEmail}</p>
                    </div>
                    <StatusBadge status={sub.status} />
                  </div>
                  <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                    <span>Beli: {new Date(sub.purchaseDate).toLocaleDateString('id-ID')}</span>
                    <span>Akhir: {new Date(sub.expiryDate).toLocaleDateString('id-ID')}</span>
                  </div>
                  <div className="flex gap-3 mt-3 pt-3 border-t border-gray-100">
                    <Link to={`/subscriptions/${sub.id}`} className="text-primary-600 text-xs font-medium">Detail</Link>
                    {isAdmin && (
                      <>
                        <Link to={`/subscriptions/${sub.id}/edit`} className="text-yellow-600 text-xs font-medium">Edit</Link>
                        <button onClick={() => handleDelete(sub.id)} className="text-red-600 text-xs font-medium">Hapus</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="table-header">
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Email Zoom</th>
                    <th className="px-4 py-3">Beli</th>
                    <th className="px-4 py-3">Berakhir</th>
                    <th className="px-4 py-3">Paket</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {subscriptions.map((sub) => (
                    <tr key={sub.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="font-medium">{sub.user?.name}</p>
                        <p className="text-sm text-gray-500">{sub.user?.department}</p>
                      </td>
                      <td className="px-4 py-3 text-sm">{sub.zoomAccountEmail}</td>
                      <td className="px-4 py-3 text-sm">{new Date(sub.purchaseDate).toLocaleDateString('id-ID')}</td>
                      <td className="px-4 py-3 text-sm">{new Date(sub.expiryDate).toLocaleDateString('id-ID')}</td>
                      <td className="px-4 py-3 text-sm">{sub.planType || '-'}</td>
                      <td className="px-4 py-3"><StatusBadge status={sub.status} /></td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Link to={`/subscriptions/${sub.id}`} className="text-primary-600 hover:text-primary-700">Detail</Link>
                          {isAdmin && (
                            <>
                              <Link to={`/subscriptions/${sub.id}/edit`} className="text-yellow-600 hover:text-yellow-700">Edit</Link>
                              <button onClick={() => handleDelete(sub.id)} className="text-red-600 hover:text-red-700">Hapus</button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    active: 'bg-green-100 text-green-800',
    expiring_soon: 'bg-yellow-100 text-yellow-800',
    expired: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-800'
  };
  const labels = { active: 'Aktif', expiring_soon: 'Habis', expired: 'Berakhir', cancelled: 'Batal' };
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status]}`}>
      {labels[status]}
    </span>
  );
};

export default Subscriptions;
